/**
 * RetailerAccessEditor — a retailer manager sets a colleague's role and access,
 * the same ticks as Trusscore's editor but limited to what the manager may hand
 * out (`assignable` from retailer-admin-members.php): nodes they hold whole and
 * stores inside their own access. Translated (customer-facing).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, MapPin, Building2, CheckSquare, Square } from 'lucide-react';
import {
  retailerMemberAction,
  type RetailerMemberRow, type RetailerRole, type RetailerAssignable, type RetailerAccessGrant,
} from '../../services/retailerAdminApi';

interface Props {
  /** Every row this person holds in the tree (one per node). */
  rows: RetailerMemberRow[];
  assignable: RetailerAssignable;
  /** The manager's own node (used as the tree root when they hold nothing above it). */
  ownRetailerId: number;
  ownRetailerName: string;
  onClose: () => void;
  onSaved: (member: RetailerMemberRow) => void;
  /** Maps a server error code to a translated message. */
  describeError: (code: string) => string;
}

export const RetailerAccessEditor: React.FC<Props> = ({ rows, assignable, ownRetailerId, ownRetailerName, onClose, onSaved, describeError }) => {
  const { t } = useTranslation('retaileradmin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<RetailerRole>(rows.some(r => r.status === 'approved' && r.role === 'manager') ? 'manager' : (rows[0]?.role ?? 'viewer'));
  const [nodes, setNodes] = useState<Set<number>>(new Set());
  const [locs, setLocs] = useState<Set<number>>(new Set());
  const [includeChildren, setIncludeChildren] = useState(true);
  const [dirty, setDirty] = useState(false);

  // The tree as the manager may hand it out: nodes they hold whole (tickable),
  // plus groups for stores they hold under nodes they do not hold whole.
  const tree = useMemo(() => {
    const whole = new Map(assignable.nodes.map(n => [n.id, n]));
    const root = assignable.nodes.find(n => n.parentId === null) ?? null;
    const groups = new Map<number, { id: number; name: string; whole: boolean; includeChildren: boolean; locations: RetailerAssignable['locations'] }>();
    for (const n of assignable.nodes) groups.set(n.id, { id: n.id, name: n.name, whole: true, includeChildren: n.includeChildren, locations: [] });
    for (const l of assignable.locations) {
      if (!groups.has(l.retailerId)) groups.set(l.retailerId, { id: l.retailerId, name: l.retailerName ?? (l.retailerId === ownRetailerId ? ownRetailerName : `#${l.retailerId}`), whole: false, includeChildren: false, locations: [] });
      groups.get(l.retailerId)!.locations.push(l);
    }
    const ordered = Array.from(groups.values()).sort((a, b) => (root && a.id === root.id ? -1 : root && b.id === root.id ? 1 : a.name.localeCompare(b.name)));
    return { root, whole, groups: ordered };
  }, [assignable, ownRetailerId, ownRetailerName]);

  useEffect(() => {
    const n = new Set<number>(); const l = new Set<number>(); let inc = true;
    for (const r of rows) {
      if (r.status === 'rejected') continue;
      if (r.allLocations) { n.add(r.retailerId); if (tree.root && r.retailerId === tree.root.id) inc = r.includeChildren; }
      else r.locations.forEach(x => l.add(x.id));
    }
    setNodes(n); setLocs(l); setIncludeChildren(inc); setDirty(false);
  }, [rows, tree.root]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const rootOn = tree.root ? nodes.has(tree.root.id) : false;
  const rootCoversAll = rootOn && includeChildren && !!tree.root?.includeChildren;
  const nodeOn = (id: number) => rootCoversAll || nodes.has(id);
  const nodeLocked = (id: number) => rootCoversAll && id !== tree.root?.id;
  const toggleNode = (id: number) => { setDirty(true); setNodes(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleLoc = (id: number) => { setDirty(true); setLocs(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  const grants = (): RetailerAccessGrant[] => {
    const out: RetailerAccessGrant[] = [];
    if (tree.root && rootOn) out.push({ type: 'retailer', id: tree.root.id, includeChildren: includeChildren && tree.root.includeChildren });
    if (!rootCoversAll) {
      for (const g of tree.groups) {
        if (tree.root && g.id === tree.root.id) continue;
        if (g.whole && nodes.has(g.id)) out.push({ type: 'retailer', id: g.id });
      }
      for (const id of locs) {
        const l = assignable.locations.find(x => x.id === id);
        if (l && !nodeOn(l.retailerId)) out.push({ type: 'location', id });
      }
    }
    return out;
  };
  const anyTicked = grants().length > 0;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const m = await retailerMemberAction({ action: 'set_access', id: rows[0].id, role, grants: grants() });
      onSaved(m); onClose();
    } catch (e) {
      setError(describeError(e instanceof Error ? e.message : ''));
    } finally {
      setBusy(false);
    }
  };

  const person = [rows[0]?.firstName, rows[0]?.lastName].filter(Boolean).join(' ') || rows[0]?.email || '';
  const Box: React.FC<{ on: boolean; locked: boolean }> = ({ on, locked }) => on ? <CheckSquare size={16} className={locked ? 'text-secondary' : 'text-primary'} /> : <Square size={16} className="text-muted" />;

  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="ra-access-title">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2" id="ra-access-title"><ShieldCheck size={18} /> {t('users.access.title', { name: person })}</h5>
                <div className="text-muted small">{rows[0]?.email}</div>
              </div>
              <button type="button" className="btn-close" aria-label={t('users.access.cancel')} disabled={busy} onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 small">{error}</div>}
              <div className="mb-3">
                <div className="small fw-semibold mb-1">{t('users.columns.role')}</div>
                {(['viewer', 'manager'] as RetailerRole[]).map(r => (
                  <div key={r} className="form-check">
                    <input id={`ra-role-${r}`} type="radio" className="form-check-input" name="ra-role" checked={role === r} disabled={busy} onChange={() => { setRole(r); setDirty(true); }} />
                    <label htmlFor={`ra-role-${r}`} className="form-check-label"><strong>{t(`roles.${r}`)}</strong> <span className="text-muted small">— {t(`users.roleHelp.${r}`)}</span></label>
                  </div>
                ))}
              </div>

              <div className="small fw-semibold mb-1">{t('users.columns.access')}</div>
              <div className="border rounded p-2 mb-2" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                {tree.groups.map(g => {
                  const isRoot = !!tree.root && g.id === tree.root.id;
                  const on = nodeOn(g.id);
                  const locked = nodeLocked(g.id);
                  return (
                    <div key={g.id} className={isRoot ? '' : 'ms-3 mt-1'}>
                      {g.whole ? (
                        <div className={`d-flex align-items-center gap-2 py-1 ${locked ? 'text-muted' : ''}`} role="button" onClick={() => !busy && !locked && toggleNode(g.id)}>
                          <Box on={on} locked={locked} />
                          {!isRoot && <span className="text-muted small">↳</span>}
                          <Building2 size={14} className="text-secondary" />
                          <span className="fw-semibold">{isRoot ? t('users.access.entire', { name: g.name }) : t('users.access.allOf', { name: g.name })}</span>
                          <span className="text-muted small">— {t('users.access.includingLater')}</span>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2 py-1 text-muted">
                          <span style={{ width: 16 }} />
                          {!isRoot && <span className="small">↳</span>}
                          <Building2 size={14} />
                          <span className="fw-semibold">{g.name}</span>
                          <span className="small">— {t('users.access.onlyYourLocations')}</span>
                        </div>
                      )}
                      {isRoot && g.whole && rootOn && g.includeChildren && tree.groups.length > 1 && (
                        <div className="form-check form-switch ms-4 mb-1">
                          <input id="ra-inc" type="checkbox" className="form-check-input" role="switch" checked={includeChildren} disabled={busy} onChange={e => { setIncludeChildren(e.target.checked); setDirty(true); }} />
                          <label htmlFor="ra-inc" className="form-check-label small">{t('users.access.includeChildren')}</label>
                        </div>
                      )}
                      {g.locations.map(l => {
                        const lOn = on || locs.has(l.id);
                        return (
                          <div key={l.id} className={`d-flex align-items-center gap-2 py-1 ${isRoot ? 'ms-4' : 'ms-5'} ${on ? 'text-muted' : ''}`} role="button" onClick={() => !busy && !on && toggleLoc(l.id)}>
                            <Box on={lOn} locked={on} />
                            <MapPin size={13} className="text-secondary" />
                            <span>{l.name}{l.city ? <span className="text-muted"> · {l.city}</span> : null}</span>
                          </div>
                        );
                      })}
                      {g.locations.length === 0 && g.whole && <div className="text-muted small ms-5">{t('users.access.noLocationsYet')}</div>}
                    </div>
                  );
                })}
              </div>
              <div className={`small ${anyTicked ? 'text-muted' : 'text-danger fw-semibold'}`}>{anyTicked ? t('users.access.hint') : t('users.access.nothingTicked')}</div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={onClose}>{t('users.access.cancel')}</button>
              <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" disabled={busy || !anyTicked || !dirty} onClick={() => void save()}>
                {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />} {t('users.access.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

export default RetailerAccessEditor;
